import { ReactNode, useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { KEY_CODES } from '#utils/constants';

interface DropdownFilterItem {
  value: string;
  text: ReactNode;
}

interface DropdownFilterBoxProps {
  width?: number;
  dropdownItems: DropdownFilterItem[];
  filter: string;
  filterKey: string;
  filterPlaceholder?: string;
  dispatch: (action: unknown) => void;
  setFilter: (value: string) => unknown;
  setFilterKey: (key: string) => unknown;
  setFilterForQuery: (value: string) => void;
}

export default function DropdownFilterBox(props: DropdownFilterBoxProps) {
  const {
    width,

    dropdownItems,
    filter,
    filterKey,
    filterPlaceholder,

    dispatch,
    setFilter,
    setFilterKey,
    setFilterForQuery,
  } = props;

  // Local state to track input before submitting
  const [localFilter, setLocalFilter] = useState(filter);

  // Sync localFilter with filter prop when it changes externally
  useEffect(() => {
    setLocalFilter(filter);
  }, [filter]);

  // Only change filter type, let Redux handle the skip
  const handleFilterKeyChange = (newFilterKey: string) => {
    dispatch(setFilterKey(newFilterKey));
  };

  // Trigger search with current filter value and type
  const handleSearch = () => {
    // First update the Redux state with the local filter value
    const cleanFilter = localFilter.trim();
    dispatch(setFilter(cleanFilter));

    // Force query refresh by triggering the action with the clean filter value
    // This will ensure the filter is updated without showing timestamps in the UI
    setFilterForQuery(cleanFilter);
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      square
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: width || 500,
      }}
    >
      <IconButton
        type="button"
        sx={{ p: '10px' }}
        aria-label="search"
        onClick={handleSearch}
      >
        <SearchIcon />
      </IconButton>
      <FormControl sx={{ ml: 1, minWidth: 90 }} variant="outlined">
        <Select
          value={filterKey}
          onChange={(e) => handleFilterKeyChange(e.target.value)}
          input={<InputBase />}
        >
          {
            dropdownItems.map((itm) => (
              <MenuItem
                key={itm.value}
                value={itm.value}
              >
                {itm.text}
              </MenuItem>
            ))
          }
        </Select>
      </FormControl>
      <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
      <InputBase
        sx={{ ml: 1, flex: 1 }}
        placeholder={filterPlaceholder}
        value={localFilter}
        onChange={(e) => {
          const newValue = e.target.value;
          setLocalFilter(newValue);

          // If the input is cleared completely, trigger a search to show all results
          if (newValue === '' && localFilter !== '') {
            // Small delay to ensure the UI updates first
            setTimeout(() => {
              dispatch(setFilter(''));
              // Use empty string instead of timestamp for clearing
              setFilterForQuery('');
            }, 100);
          }
        }}
        onKeyUp={(e) => {
          if (e.keyCode === KEY_CODES.ENTER) {
            handleSearch();
          }
        }}
      />
    </Paper>
  );
}
